import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, Select, MenuItem, FormControl, InputLabel, useTheme } from "@mui/material";
import colors from "../../color";
import { useTranslation } from "react-i18next";

const QuestionnaireGroup = ({
  questions = [],
  enableRanking = false,
  onRankingChange: onPriorityChangeProp,
  prioritizedYesIndexes = [],
  setIsValid = null,
}) => {
  const [localRanking, setRanking] = useState([]);
  const onPriorityChange = useCallback(onPriorityChangeProp, [onPriorityChangeProp]);
  const { t } = useTranslation();
  const theme = useTheme();

  useEffect(() => {
    if (questions.length > 0 && prioritizedYesIndexes.length > 0) {
      const initialRanking = questions.map((_, index) =>
        prioritizedYesIndexes.includes(index) ? prioritizedYesIndexes.indexOf(index) + 1 : ""
      );

      // Only set the ranking if localRanking is empty (not set yet)
      if (localRanking.length === 0) {
        setRanking(initialRanking);
      }
    }
  }, [questions, prioritizedYesIndexes, localRanking]);

  useEffect(() => {
    if (setIsValid) {
      const requiredRankings = Math.min(3, questions.length);
      const nonEmptyRankings = localRanking.filter((rank) => rank !== "").length;
      setIsValid(nonEmptyRankings >= requiredRankings);
    }
  }, [localRanking, questions, setIsValid]);

  const handleRankingChange = (index, rank) => {
    const updatedRanking = [...localRanking];

    // Clear existing rank if replacing it
    const existingIndex = updatedRanking.indexOf(rank);
    if (existingIndex !== -1) {
      updatedRanking[existingIndex] = "";
    }

    // Set the new rank
    updatedRanking[index] = rank;
    setRanking(updatedRanking);

    // Pass updated rankings back to the parent
    onPriorityChange(
      updatedRanking.map((priority, i) => ({
        rank: priority,
        index: i,
        questionText: questions[i],
      }))
    );
  };

  const priorityOptions = Array.from({ length: Math.min(3, questions.length) }, (_, i) => i + 1);

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={1}
      style={{
        color: colors.textPrimary,
      }}
    >
      {questions && questions.length > 0 ? (
        questions.map((question, index) => (
          <Box
            key={index}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            style={{
              color: colors.textPrimary,
            }}
          >
            <Box flex="1">
              <Typography variant="h6" sx={{ fontWeight: 400, color: colors.textPrimary }}>
                {index + 1}. {t(question)}
              </Typography>
            </Box>
            {enableRanking && (
              <FormControl
                variant="filled"
                size="small"
                sx={{
                  minWidth: 130,
                  "& .MuiFilledInput-root": {
                    "&:before": {
                      borderBottom: "none", // Remove the default underline
                    },
                    "&:after": {
                      borderBottom: "none", // Remove the focused underline
                    },
                  },
                }}
              >
                <InputLabel id={`priority-${index}`}>{t("text_priority")}</InputLabel>
                <Select
                  value={localRanking[index] || ""}
                  onChange={(e) => handleRankingChange(index, e.target.value)}
                  labelId={`priority-${index}`}
                  label={t("text_priority")}
                  sx={{
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: colors.textPrimary,
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: colors.textPrimary,
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: colors.textPrimary,
                    },
                  }}
                >
                  {priorityOptions.map((priority) => (
                    <MenuItem key={priority} value={priority}>
                      <Typography
                        sx={{
                          color: colors.textPrimary,
                        }}
                      >
                        {priority}
                      </Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        ))
      ) : (
        <Box>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 400,
              textAlign: "left",
              color: colors.textPrimary,
              fontStyle: "italic",
            }}
          >
            {t("none_were_selected")}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default QuestionnaireGroup;
